import { Context, useContext, useCallback, useMemo } from "react"
import { capitalize, get } from "lodash"
import { useAnalytics } from "connected-components/analytics"
import { usePositionSetBetaInstrument } from "connected-components/asset-view/helpers"
import { useAssetViewHeaderHandlers } from "connected-components/asset-view/hooks/use-asset-view-header-handlers"
import { IAssetViewTemplateContextValue } from "connected-components/asset-view/templates/types"
import { IDashboardContextValue } from "connected-components/dashboard/context/types"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import { useNormalPerformancePricingRefreshWarning } from "gatherers/hooks/use-normal-performance-pricing-refresh-warning"
import { ONE_DAY } from "helpers/dates"
import { getPerformanceFilterInstrumentColumns } from "helpers/metrics"
import { METRIC_TYPES } from "helpers/metrics/constants"
import { useAssetViewDashboardMetrics } from "helpers/metrics/hooks"
import { ATTRIBUTION_METHODS } from "helpers/risk"
import { InstrumentWithMetadata } from "helpers/types/instruments/metadata"
import { DESC } from "helpers/sorts"
import ContributorsTable from "renderers/contributors-datagrid"
import { useModelBetaFields } from "resources/beta"
import { ClassificationMeta } from "resources/classifications"
import { ModelMetadata } from "resources/models"
import { useIsOpPro } from "../../../../resources/account"

interface AssetViewSummaryProps {
  modelMetadata: ModelMetadata
  context: Context<IAssetViewTemplateContextValue & IDashboardContextValue>
  instrumentsWithMetadata: Record<string, InstrumentWithMetadata>
  highlightedRowIds: string[]
  disableActiveTrading: boolean
  instrumentColumns: string[]
  classifications: ClassificationMeta[]
}

export default function AssetViewSummary({
  modelMetadata,
  context,
  instrumentsWithMetadata,
  highlightedRowIds,
  disableActiveTrading,
  instrumentColumns,
  classifications,
}: AssetViewSummaryProps) {
  const {
    instrument: original,
    base: contextBase,
    groupBy,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setTradedPosition,
    selectedTableLevels,
    setSelectedTableLevels,
    isExpanded,
    isEndVsStartDateComparison,
    modelId,
    isIntradayEnabled,
    period,
    performanceEndDate,
    performanceStartDate,
    startDate,
    endDate,
  } = useContext(context)

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useAssetViewHeaderHandlers({
    track: analytics.track,
  }) as {
    handleSortChanged: (sortMetricId: string, sortDirection: string) => void
    handleTableNavigationChanged: (tableLevels: string[]) => void
  }

  const isOpPro = useIsOpPro()
  const [modelBetaFields] = useModelBetaFields(modelId)
  const betaInstrument = usePositionSetBetaInstrument(instrumentsWithMetadata.original, modelMetadata)
  const getBetaDataPath = useCallback((path: string) => {
    return `beta.${path}`
  }, [])

  const apiStartDate = period === ONE_DAY ? performanceEndDate : performanceStartDate
  const attributionMethod = contextBase ? ATTRIBUTION_METHODS.MCTE : ATTRIBUTION_METHODS.MCAR

  const gatherers = useTemplateGatherers(context, "summary", {
    classifications,
    betaInstrument,
    attributionMethod,
    betaFields: modelBetaFields,
    from: apiStartDate,
    to: performanceEndDate,
    selectedMetricDisplay: METRIC_TYPES.CURRENCY_PERFORMANCE,
  })

  useNormalPerformancePricingRefreshWarning({ gatherers, startDate, endDate })

  const hasReferenceEquity = get(instrumentsWithMetadata?.original, "referenceEquity") != null

  const prependMetrics = useMemo(() => [{ id: "description" }], [])

  const metricsAndCategories = useMemo(() => [
    {
      category: "performance",
      selectedMetricDisplay: METRIC_TYPES.CURRENCY_PERFORMANCE,
      selectedCategory: "",
      filterInstrumentColumns: getPerformanceFilterInstrumentColumns(isEndVsStartDateComparison),
    },
    ...(!isOpPro ? [{
      category: "risk",
      selectedCategory: "",
      categoryLabel: "Summary Risk",
    }] : []),
    {
      category: "beta",
      betaFields: modelBetaFields || [],
      betaInstrument,
      getBetaDataPath,
    },
  ], [
    isOpPro,
    isEndVsStartDateComparison,
    modelBetaFields,
    betaInstrument,
    getBetaDataPath,
  ])

  const dashboardMetricsOptions = useMemo(() => ({
    instrumentColumns,
    isActive: !!contextBase,
    modelMetadata,
    instrumentsWithMetadata,
    setTradedPosition,
    isExpanded,
    disableActiveTrading,
    isEndVsStartDateComparison,
    isIntradayEnabled,
    hasReferenceEquity,
    prependMetrics,
  }), [
    instrumentColumns,
    contextBase,
    modelMetadata,
    instrumentsWithMetadata,
    setTradedPosition,
    isExpanded,
    disableActiveTrading,
    isEndVsStartDateComparison,
    isIntradayEnabled,
    hasReferenceEquity,
    prependMetrics,
  ])

  const metrics = useAssetViewDashboardMetrics(
    metricsAndCategories,
    original,
    rebalanced,
    comparison,
    dashboardMetricsOptions,
  )

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0] as keyof typeof INSTRUMENTS].suffix

  return <ContributorsDataIntegrator
    instrumentsWithMetadata={instrumentsWithMetadata}
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    onModelProviderIds={undefined}
    rendererProps={{
      highlightedRowIds,
      groupByName: groupBy?.label,
      isPositionAttributeGroup: !!groupBy?.isPositionAttribute,
      defaultSort: {
        path: `marketValue${capitalize(defaultSortSuffix)}`,
        direction: DESC,
      },
      downloadKey: "assets.summary",
      downloadName: "Assets - Summary",
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
      context,
      showSecurityName: true,
      idLabel: "Ticker",
    }}
  />
}
